/**
 * Règles d'étiquetage, calquées sur celles du serveur (contrat 1.4) et
 * vérifiées contre l'API : rognage des espaces de bord, comparaison
 * insensible à la casse, **casse d'origine conservée**, 40 caractères par
 * étiquette et 20 par document.
 *
 * Refaire ces contrôles ici ne remplace pas ceux du serveur : il s'agit de
 * refuser tout de suite ce qu'il refuserait de toute façon, plutôt que de
 * laisser partir une requête pour récolter un 422 illisible.
 */

export const MAX_TAG_LENGTH = 40
export const MAX_TAGS_PER_DOCUMENT = 20

/**
 * Rogne les espaces de bord, et rien d'autre. Le serveur ne réduit ni les
 * espaces internes ni la casse : « mise   en ligne » lui revient tel quel.
 */
export function normalizeTag(raw: string): string {
  return raw.trim()
}

/**
 * Deux étiquettes désignent-elles la même chose ? La comparaison suit celle du
 * serveur — minuscules, sans repli sur les accents : « Été » et « ete » restent
 * deux étiquettes distinctes de part et d'autre.
 */
export function sameTag(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

export function hasTag(tags: readonly string[], tag: string): boolean {
  return tags.some((item) => sameTag(item, tag))
}

export type TagRefusal = 'vide' | 'trop-longue' | 'deja-presente' | 'trop-nombreuses'

export const TAG_REFUSAL_MESSAGES: Record<TagRefusal, string> = {
  vide: 'Une étiquette ne peut pas être vide.',
  'trop-longue': `Une étiquette ne peut pas dépasser ${String(MAX_TAG_LENGTH)} caractères.`,
  'deja-presente': 'Cette étiquette est déjà là.',
  'trop-nombreuses': `Un document ne peut pas porter plus de ${String(MAX_TAGS_PER_DOCUMENT)} étiquettes.`,
}

/**
 * Ce qui empêcherait d'ajouter cette étiquette, ou `null` si rien ne s'y
 * oppose. Séparé de l'ajout pour que l'appelant puisse expliquer le refus.
 */
export function tagRefusal(tags: readonly string[], raw: string): TagRefusal | null {
  const tag = normalizeTag(raw)
  if (tag === '') return 'vide'
  if (tag.length > MAX_TAG_LENGTH) return 'trop-longue'
  if (hasTag(tags, tag)) return 'deja-presente'
  if (tags.length >= MAX_TAGS_PER_DOCUMENT) return 'trop-nombreuses'
  return null
}

/**
 * Ajoute une étiquette si elle est recevable. La liste d'origine est renvoyée
 * telle quelle en cas de refus : l'appelant compare les références s'il veut
 * savoir, ou interroge `tagRefusal` avant.
 */
export function addTag(tags: readonly string[], raw: string): string[] {
  if (tagRefusal(tags, raw) !== null) return [...tags]
  return [...tags, normalizeTag(raw)]
}

export function removeTag(tags: readonly string[], tag: string): string[] {
  return tags.filter((item) => !sameTag(item, tag))
}

/**
 * Bascule une étiquette dans une sélection — le geste du filtre, où cliquer
 * une étiquette déjà retenue la retire.
 */
export function toggleTag(tags: readonly string[], tag: string): string[] {
  return hasTag(tags, tag) ? removeTag(tags, tag) : [...tags, normalizeTag(tag)]
}

/**
 * Découpe une liste d'URL. Les étiquettes vides et les doublons de casse
 * disparaissent, et le tout est plafonné : le contrat accepte au plus dix
 * étiquettes dans un filtre, au-delà le serveur répondrait 422.
 */
export const MAX_TAGS_PER_FILTER = 10

export function parseTags(value: string | undefined): string[] {
  if (!value) return []
  const tags: string[] = []
  for (const part of value.split(',')) {
    const tag = normalizeTag(part)
    if (tag === '' || tag.length > MAX_TAG_LENGTH || hasTag(tags, tag)) continue
    tags.push(tag)
    if (tags.length === MAX_TAGS_PER_FILTER) break
  }
  return tags
}

/** Sérialise pour l'URL et pour la query ; `undefined` quand il n'y a rien. */
export function serializeTags(tags: readonly string[]): string | undefined {
  return tags.length > 0 ? tags.join(',') : undefined
}
